import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { User } from '@/shared/types/api.types';
import { APP_CONFIG } from '@/shared/config/app.config';

interface AuthState {
  user: User | null;
  token: string | null;
  isInitialized: boolean;
}

interface AuthActions {
  setAuth: (user: User, token: string) => void;
  updateUser: (updates: Partial<User>) => void;
  clearAuth: () => void;
  initialize: () => void;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  persist(
    immer((set) => ({
      // Initial state
      user: null,
      token: null,
      isInitialized: false,

      // Actions
      setAuth: (user, token) =>
        set((state) => {
          state.user = user;
          state.token = token;
          state.isInitialized = true;
        }),

      updateUser: (updates) =>
        set((state) => {
          if (state.user) {
            Object.assign(state.user, updates);
          }
        }),

      clearAuth: () =>
        set((state) => {
          state.user = null;
          state.token = null;
          state.isInitialized = true;
        }),

      initialize: () =>
        set((state) => {
          state.isInitialized = true;
        }),
    })),
    {
      name: APP_CONFIG.STORAGE.KEYS.AUTH_TOKEN,
      partialize: (state) => ({
        user: state.user,
        token: state.token,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.isInitialized = true;
          
          // Check if token is expired
          if (state.token && state.user) {
            const tokenData = parseJWT(state.token);
            if (tokenData && tokenData.exp * 1000 < Date.now()) {
              state.user = null;
              state.token = null;
            }
          }
        }
      },
    }
  )
);

// Helper function to parse JWT token
function parseJWT(token: string): { exp: number } | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}